import type { MarkData, FieldMapping } from '../config/types';
import type { DataTable, Column, DataValue } from '@tableau/extensions-api-types';

const DEFAULT_INLINE_SUMMARY_FIELDS = [
  'alert_id',
  'ALERT TYPE',
  'ALERT STATUS',
  'ALERT CATEGORY',
  'HCP NAME',
];

export function mapSelectedMark(
  table: DataTable,
  fieldMapping: FieldMapping
): MarkData {
  if (table.totalRowCount === 0) {
    return { key: null, fields: {} };
  }

  const columns = table.columns;
  const firstRow = table.data[0];

  const fields: Record<string, unknown> = {};

  columns.forEach((col: Column, index: number) => {
    const value = extractValue(firstRow[index]);

    if (fieldMapping.includeFields && fieldMapping.includeFields.length > 0) {
      if (matchesFieldName(col.fieldName, fieldMapping.includeFields)) {
        fields[col.fieldName] = value;
      }
    } else {
      fields[col.fieldName] = value;
    }
  });

  const keyValue = fieldMapping.keyField
    ? getFieldValue({ key: null, fields }, fieldMapping.keyField)
    : null;
  const key = keyValue === null || keyValue === undefined || keyValue === '' ? null : String(keyValue);

  return { key, fields };
}

function extractValue(dataValue: DataValue): unknown {
  if (dataValue.value === null || dataValue.value === undefined) {
    return null;
  }

  return dataValue.nativeValue ?? dataValue.value;
}

export function formatMarkSummary(markData: MarkData): string {
  if (Object.keys(markData.fields).length === 0) {
    return 'No data';
  }

  const lines: string[] = [];
  const primaryFieldName = findPrimaryFieldName(markData);

  if (markData.key) {
    lines.push(`${primaryFieldName ?? 'Selected Alert'}: ${markData.key}`);
  }

  for (const [field, value] of Object.entries(markData.fields)) {
    if (field === primaryFieldName) continue;
    const displayValue = value === null ? '(empty)' : String(value);
    lines.push(`${field}: ${displayValue}`);
  }

  return lines.join('\n');
}

export function formatInlineMarkSummary(
  markData: MarkData,
  preferredFields = DEFAULT_INLINE_SUMMARY_FIELDS
): string {
  if (Object.keys(markData.fields).length === 0) {
    return 'No selection';
  }

  const values = preferredFields
    .map((fieldName) => getFieldValue(markData, fieldName))
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => String(value));

  if (values.length > 0) {
    return values.join(' | ');
  }

  if (markData.key) {
    return markData.key;
  }

  const firstValue = Object.values(markData.fields).find(
    (value) => value !== null && value !== undefined && value !== ''
  );
  return firstValue === undefined ? 'No selection' : String(firstValue);
}

export function getFieldValue(markData: MarkData, fieldName: string): unknown {
  const normalizedFieldName = normalizeFieldName(fieldName);

  for (const [candidateFieldName, value] of Object.entries(markData.fields)) {
    if (normalizeFieldName(candidateFieldName) === normalizedFieldName) {
      return value;
    }
  }

  return null;
}

function findPrimaryFieldName(markData: MarkData): string | null {
  if (!markData.key) {
    return null;
  }

  for (const [field, value] of Object.entries(markData.fields)) {
    if (value !== null && value !== undefined && String(value) === markData.key) {
      return field;
    }
  }

  return null;
}

function matchesFieldName(fieldName: string, candidates: string[]): boolean {
  const normalizedFieldName = normalizeFieldName(fieldName);
  return candidates.some((candidate) => normalizeFieldName(candidate) === normalizedFieldName);
}

function normalizeFieldName(fieldName: string): string {
  return fieldName.trim().toLowerCase();
}
